'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseInactivityTimerOptions {
  delay: number;
  events?: string[];
  showOnce?: boolean;
}

export function useInactivityTimer({
  delay,
  events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'],
  showOnce = false,
}: UseInactivityTimerOptions) {
  const [isInactive, setIsInactive] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    // If showOnce is true and already triggered, don't start a new timer
    if (showOnce && hasTriggered) {
      return;
    }

    setIsInactive(false);
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    if (delay > 0) {
      timeoutId.current = setTimeout(() => {
        setIsInactive(true);
        if (showOnce) {
          setHasTriggered(true);
        }
      }, delay);
    }
  }, [delay, showOnce, hasTriggered]);

  const resetInactiveState = useCallback(() => {
    setIsInactive(false);
  }, []);

  useEffect(() => {
    const handleActivity = () => {
      setIsInactive(false);
      if (showOnce && hasTriggered) {
        return;
      }
      resetTimer();
    };

    resetTimer();

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [events, resetTimer, showOnce, hasTriggered]);

  return { isInactive, resetInactiveState };
}
